import booleanIntersects from "@turf/boolean-intersects";
import * as turf from '@turf/turf'
import { booleanPointInPolygon, centroid, distance, explode, nearest, nearestPointOnLine } from "@turf/turf";
import { layers, smokeSensitiveAreasLayers } from "../config/map.constants";
import { getFeaturesByBounds } from "./map.service";
import { getJson, saveJson } from './offline/cache.service'

/**
 * Fetch Smoke sensitive area features from map servers and cache them locally
 * @param {xmin:number, ymin:number, xmax:number, ymax:number} bounds 
 */
export const fetchAndCacheSSAs = async (bounds) => {
	const ssas = await getSSAsByBounds(bounds);
	saveJson('ssas', ssas)
}

/**
 * Retrieves Smoke sensitive area features from local storage
 * @returns {Array<{type: "FeatureCollection", features: Feature[], layer: LayerInfo, style: any}> | null}
 */
export const getCachedSSAs = () => {
	const ssas = getJson('ssas').map(info => ({...info, markerImage: layers[info.layerId].markerImage}))
	return ssas;
}


/**
 * Fetches all Smoke sensitive area features within the given boundaries.
 * @param {xmin:number, ymin:number, xmax:number, ymax:number} bounds 
 * @returns {Array<{type: "FeatureCollection", features: Feature[], layerId: string}>}
 */
export const getSSAsByBounds = async (bounds) => {
	const ssasLayers = Object.values(smokeSensitiveAreasLayers).filter(l => !l.geojson)
	const features = [];
	const promises = ssasLayers.map(async layer => {
		try {
			const res = await getFeaturesByBounds(layer.url, bounds, true, layer.arcGisOpts.outFields)
			features.push({
				type: "FeatureCollection",
				features: res,
				layerId: layer.id
			});
			return true;
		} catch(e) { return false;}
	});
	await Promise.all(promises);
	return features;
}

export const querySSAsInsidePolygon = (map, polygon) => {
	const layerIds = Object.values(smokeSensitiveAreasLayers).map(l => l.id);
	let features = map.queryRenderedFeatures(null, { layers: layerIds });
	return features.filter(f => booleanIntersects(f, polygon));
}

export const getSSABasicInformationFromFeature = (ssaFeature) => {
	const type = layers[ssaFeature.layer.id].label;
	const addressField = layers[ssaFeature.layer.id].addressField;
	const layerId = ssaFeature.layer.id;

	return layerId === layers.arkansas_city_limits.id ? {
		name: ssaFeature.properties.city_name,
		type,
		address: ''

	} :  layerId === layers.arkansas_highways.id ? {
		name: 'Highway ' + ssaFeature.properties.label,
		type: '',
		address: ''

	} :  layerId === layers.arkansas_care_facilities.id ? {
		name: ssaFeature.properties.facility_name,
		type,
		address: ssaFeature.properties[addressField]

	} : {
		name: ssaFeature.properties.name,
		type,
		address: ssaFeature.properties[addressField]
	}
}

/**
 * Given an SSA Feature find nearest SSA from origin
 * Different methods depending on feature type (point, line, polygon)
 * @param {Feature} ssa 
 */
export const findNearestSSA = (originPoint, ssa, inArea) => {
	if (ssa.layer.type === 'line') {
		const ssaIntersection = turf.lineIntersect(inArea.geometry, ssa.geometry);
		const nearest = turf.nearestPoint(originPoint, ssaIntersection)
		return distance(nearest, originPoint, {units: 'miles'});
	} else if (ssa.layer.type === 'fill') {
		const ssaIntersection = turf.intersect(inArea, ssa.geometry);
		const isInside = turf.inside(originPoint, ssaIntersection);
		return isInside ? 0 : distanceToPolygon({ point: originPoint, polygon: ssaIntersection });
	} else {
		return distance(ssa, originPoint, {units: 'miles'});
	}
}

// Returns distance in meters (negative values for points inside) from a point to the edges of a polygon
function distanceToPolygon({ point, polygon }) {
  if (polygon.type === "Feature") { polygon = polygon.geometry }
  let distance;
  if (polygon.type === "MultiPolygon") {
    distance = polygon.coordinates
      .map(coords => distanceToPolygon({ point, polygon: turf.polygon(coords).geometry }))
      .reduce((smallest, current) => (current < smallest ? current : smallest));
  } else {
    if (polygon.coordinates.length > 1) {
      // Has holes
      const [exteriorDistance, ...interiorDistances] = polygon.coordinates.map(coords =>
        distanceToPolygon({ point, polygon: turf.polygon([coords]).geometry })
      );
      if (exteriorDistance < 0) {
        // point is inside the exterior polygon shape
        const smallestInteriorDistance = interiorDistances.reduce(
          (smallest, current) => (current < smallest ? current : smallest)
        );
        if (smallestInteriorDistance < 0) {
          // point is inside one of the holes (therefore not actually inside this shape)
          distance = smallestInteriorDistance * -1;
        } else {
          // find which is closer, the distance to the hole or the distance to the edge of the exterior, and set that as the inner distance.
          distance = smallestInteriorDistance < exteriorDistance * -1
            ? smallestInteriorDistance * -1
            : exteriorDistance;
        }
      } else {
        distance = exteriorDistance;
      }
    } else {
      // The actual distance operation - on a normal, hole-less polygon (converted to meters)
//       distance = turf.pointToLineDistance(point, turf.polygonToLineString(polygon), {units: 'miles'}) * 1000;
      distance = turf.pointToLineDistance(point, turf.polygonToLineString(polygon), {units: 'miles'});
      if (turf.booleanPointInPolygon(point, polygon)) {
        distance = distance * -1;
      }
    }
  }
  return distance
}